import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Building2, Layers, FileText, Plus, Pencil, Trash2,
  ChevronRight, ChevronDown, Upload, Boxes, Settings as SettingsIcon,
  Palette, Save, Loader2, RefreshCw, CheckCircle2, AlertTriangle,
  Inbox, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Input } from '../ui/input';
import MaterialthequeStatusCard from '../materialtheque/MaterialthequeStatusCard';
import LotKpiBar, { LotStatusPill, FloorWorkflowBar, computeLotStatus } from './LotKpiBar';
import LotThumbnail from './LotThumbnail';
import CorrespondanceBadge from './CorrespondanceBadge';
import { HudCard, HudButton, HudBadge } from '../hud';
import ProjectBriefSection from './ProjectBriefSection';
import { ApartmentPlanBlock, ApartmentPhotosBlock, ApartmentDocsBlock } from './ApartmentDossierSection';
import { getProjectVocab } from '../../lib/projectVocab';
import { FloorNameInline, FloorKindInline, FloorMoveButtons } from './FloorInlineEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

const WORKFLOW_STEPS = [
  { key: 'analyse_pre_projet', label: 'Analyse' },
  { key: 'analyse_documents', label: 'Documents' },
  { key: 'demande_validee',   label: 'En cours' },
  { key: 'livraison_beta',    label: 'Bêta' },
  { key: 'validation_client', label: 'Validation' },
  { key: 'livraison_finale',  label: 'Final' },
];

const LOT_PREVIEW_LIMIT = 20;

/**
 * ProjectSetupSection — V14.2 / Sprint C Phase 2.
 *
 * Concentre toutes les "données métier" du projet :
 *  - Structure : bâtiments / étages / lots
 *  - Fichiers : workspace 3-zones (déjà géré par ProjectFilesWorkspace)
 *  - Caractéristiques techniques (migrées depuis Settings)
 *  - Catalogue Styles/Matériaux (migré depuis Settings)
 *  - Matériauthèque IA : status card visible en permanence
 *
 * Les dialogues lourds (Building/Floor/Lot/Config) restent dans le parent
 * `ProjectDetailPage` qui pilote l'ouverture via les setters passés en props.
 */
const ProjectSetupSection = ({
  // identity
  projectId, project, navigate, pendingForBadge,
  // engineering structure data
  engTab, setEngTab,
  buildings, floors, setFloors, lots, allLots, files, setFiles,
  generations, loadingGenerations, genByLot,
  engBuildingFilter, setEngBuildingFilter,
  expandedLotsByFloor, setExpandedLotsByFloor,
  collapsedBuildings, toggleBuildingCollapse,
  collapsedFloors, toggleFloorCollapse,
  expandedLotRow, setExpandedLotRow,
  // dialog openers
  setShowBuildingDialog, setSelectedBuilding, setShowFloorDialog,
  setSelectedFloor, setShowLotDialog, setSelectedLot, setLotForm, setShowConfigDialog,
  // handlers & nav
  handleDeleteBuilding, fetchGenerations, setActiveSection,
  setAllLots, setLots,
  // workspace import file input — uses the same hidden input rendered by parent
  ProjectFilesWorkspace,
  // catalogue (preloaded by parent)
  styles, materials,
  // project-level setter for caracteristiques save
  onProjectSaved,
  // V17.0 — rendu d'un seul onglet (sans barre d'onglets) : 'structure'
  only = null,
}) => {
  const isApartment = project?.project_type_code === 'apartment_single';
  const vocab = getProjectVocab(project?.project_type_code);
  return (
    <Tabs value={only || engTab} onValueChange={setEngTab} className="space-y-6">
      {/* Matériauthèque IA status card — visible at all times */}
      {!only && <MaterialthequeStatusCard projectId={projectId} />}

      {/* Setup action bar */}
      <div
        className="flex items-center justify-between gap-3 flex-wrap"
        data-testid="engineering-action-bar"
      >
        {only ? <span className="text-[12.5px] text-[var(--c-text-muted)]">Lieu unique de configuration : bâtiments, étages (sous-sol · courant · toit) et lots. Téléchargez le modèle Excel pré-rempli, complétez-le, ré-importez-le (fusion : rien n'est supprimé).</span> : <TabsList
          className="p-1"
          style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
        >
          <TabsTrigger value="brief" data-testid="tab-brief">
            <Inbox className="w-4 h-4 mr-2" />
            Demande client
          </TabsTrigger>
          <TabsTrigger value="structure" data-testid="tab-structure">
            <Building2 className="w-4 h-4 mr-2" />
            Structure
          </TabsTrigger>
          <TabsTrigger value="files" data-testid="tab-files">
            <FileText className="w-4 h-4 mr-2" />
            Fichiers
          </TabsTrigger>
          <TabsTrigger value="caracteristiques" data-testid="tab-caracteristiques">
            <SettingsIcon className="w-4 h-4 mr-2" />
            Caractéristiques
          </TabsTrigger>
          <TabsTrigger value="catalogue" data-testid="tab-catalogue">
            <Palette className="w-4 h-4 mr-2" />
            Catalogue
          </TabsTrigger>
          <TabsTrigger value="config" className="hidden">Config</TabsTrigger>
        </TabsList>}

        <div className="flex items-center gap-2 flex-wrap">
          {only && <Button variant="outline" size="sm" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/template?fmt=xlsx`, '_blank')} data-testid="structure-template-xlsx">
            <Download className="w-3.5 h-3.5 mr-1.5" />Modèle Excel (structure actuelle)
          </Button>}
          {!only && <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/materialtheque?project=${projectId}`)}
            data-testid="eng-open-materialtheque-btn"
            className="relative"
          >
            <Boxes className="w-4 h-4 mr-1.5" />
            Matériauthèque IA
            {pendingForBadge && (
              <span
                aria-hidden
                className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
                style={{ background: '#FBBF24', boxShadow: '0 0 8px rgba(251,191,36,0.7)' }}
              />
            )}
          </Button>}
          <Button
            variant="outline"
            size="sm"
            onClick={() => document.getElementById('import-file-input').click()}
            data-testid="eng-import-btn"
          >
            <Upload className="w-4 h-4 mr-1.5" />
            Importer XLS/CSV
          </Button>
          <Button
            size="sm"
            onClick={() => setShowBuildingDialog(true)}
            data-testid="add-building-btn"
            className={isApartment ? 'hidden' : undefined}
          >
            <Plus className="w-4 h-4 mr-1.5" />
            {vocab.building}
          </Button>
        </div>
      </div>

      {/* ===== Brief client Tab (Sprint D1 — fusion Demande + Paramétrage) ===== */}
      <TabsContent value="brief" className="space-y-5">
        <ProjectBriefSection
          project={project}
          projectId={projectId}
          onSaved={onProjectSaved}
          onOpenFilesTab={() => setEngTab('files')}
        />
      </TabsContent>

      {/* ===== Structure Tab ===== */}
      <TabsContent value="structure" className="space-y-6">
        {isApartment && (
          <>
            <ApartmentPlanBlock
              projectId={projectId}
              project={project}
              onProjectSaved={onProjectSaved}
            />
            <ApartmentPhotosBlock
              projectId={projectId}
              files={files}
              setFiles={setFiles}
            />
            <ApartmentDocsBlock
              projectId={projectId}
              files={files}
              setFiles={setFiles}
              navigate={navigate}
            />
            <div className="pt-2">
              <h3 className="text-[12px] uppercase tracking-[0.18em] font-semibold mb-3"
                style={{ color: 'var(--c-text-muted)' }}>
                Pièces de l&apos;appartement
              </h3>
              <p className="text-[11.5px] mb-3" style={{ color: 'var(--c-text-faint)' }}>
                Définissez chaque pièce comme un lot (salon, chambre…) — chaque pièce peut recevoir son propre style et matériaux pour la génération 3D.
              </p>
            </div>
          </>
        )}

        {buildings.length > 1 && (
          <div className="flex items-center gap-3 flex-wrap" data-testid="eng-building-filter-wrap">
            <span className="text-[12px] uppercase tracking-[0.16em] font-semibold" style={{ color: 'var(--c-text-muted)' }}>
              Filtrer bâtiment
            </span>
            <div className="min-w-[220px]">
              <Select value={engBuildingFilter} onValueChange={setEngBuildingFilter}>
                <SelectTrigger data-testid="eng-building-filter" className="h-9">
                  <SelectValue placeholder="Tous les bâtiments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les bâtiments ({buildings.length})</SelectItem>
                  {buildings.map((b) => (
                    <SelectItem key={b.building_id} value={b.building_id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="text-[11.5px]" style={{ color: 'var(--c-text-faint)' }}>
              {engBuildingFilter === 'all'
                ? `${allLots.length} lot${allLots.length > 1 ? 's' : ''} au total`
                : (() => {
                    const b = buildings.find(x => x.building_id === engBuildingFilter);
                    if (!b) return '';
                    const flrIds = (floors[b.building_id] || []).map(f => f.floor_id);
                    const count = allLots.filter(l => flrIds.includes(l.floor_id)).length;
                    return `${count} lot${count > 1 ? 's' : ''}`;
                  })()}
            </span>
          </div>
        )}

        {buildings.length === 0 ? (
          isApartment ? (
            <div className="text-center py-10 rounded-xl" data-testid="eng-empty-apartment"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.12)' }}>
              <Inbox className="w-10 h-10 mx-auto mb-3" style={{ color: 'rgba(255,255,255,0.30)' }} />
              <p className="text-[12.5px] mb-4" style={{ color: 'var(--c-text-muted)' }}>
                {vocab.noLots}. {vocab.addBuilding} pour démarrer la configuration 3D.
              </p>
              <Button size="sm" onClick={() => setShowBuildingDialog(true)} data-testid="eng-empty-add-room">
                <Plus className="w-4 h-4 mr-1.5" /> {vocab.addLot}
              </Button>
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200" data-testid="eng-empty">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">
                Aucun bâtiment pour ce projet. Choisis comment commencer&nbsp;:
              </p>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <Button size="sm" onClick={() => setShowBuildingDialog(true)} data-testid="eng-empty-add-building">
                  <Plus className="w-4 h-4 mr-1.5" /> Créer un bâtiment
                </Button>
                <Button size="sm" variant="outline" onClick={() => document.getElementById('import-file-input').click()} data-testid="eng-empty-import">
                  <Upload className="w-4 h-4 mr-1.5" /> Importer un fichier
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate(`/materialtheque?project=${projectId}`)} data-testid="eng-empty-materialtheque">
                  <Boxes className="w-4 h-4 mr-1.5" /> Analyser un CCTP
                </Button>
              </div>
            </div>
          )
        ) : (
          <div className="space-y-4">
            {buildings
              .filter(b => engBuildingFilter === 'all' || b.building_id === engBuildingFilter)
              .map(building => (
              <div key={building.building_id} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`building-card-${building.building_id}`}>
                <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => toggleBuildingCollapse(building.building_id)}
                    className="flex items-center gap-3 min-w-0 flex-1 text-left hover:bg-gray-100/60 -m-1 p-1 rounded transition-colors"
                    data-testid={`building-toggle-${building.building_id}`}
                    aria-expanded={!collapsedBuildings.has(building.building_id)}
                  >
                    {collapsedBuildings.has(building.building_id)
                      ? <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
                    <Building2 className="w-5 h-5 text-blue-600 shrink-0" />
                    <div className="min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{building.name}</h3>
                      <p className="text-sm text-gray-500 truncate">{building.address || 'Aucune adresse'}</p>
                    </div>
                    <span className="text-xs text-gray-400 ml-2 shrink-0">
                      {(floors[building.building_id] || []).length} étage{(floors[building.building_id] || []).length > 1 ? 's' : ''}
                    </span>
                  </button>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedBuilding(building);
                        setShowFloorDialog(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Étage
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); handleDeleteBuilding(building.building_id); }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {!collapsedBuildings.has(building.building_id) && (
                <div className="divide-y divide-gray-100">
                  {(floors[building.building_id] || []).map((floor, fIdx, fArr) => {
                    const onFloorSaved = (updated) => setFloors((prev) => ({
                      ...prev,
                      [building.building_id]: (prev[building.building_id] || []).map((f) => (f.floor_id === updated.floor_id ? { ...f, ...updated } : f)),
                    }));
                    const onFloorsMoved = (list) => setFloors((prev) => ({ ...prev, [building.building_id]: list }));
                    return (
                    <div key={floor.floor_id} className="p-4" data-testid={`floor-card-${floor.floor_id}`}>
                      <div className="flex items-center justify-between mb-3 gap-3">
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleFloorCollapse(floor.floor_id)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFloorCollapse(floor.floor_id); } }}
                          className="flex items-center gap-2 min-w-0 flex-1 text-left hover:bg-gray-50 -mx-1 px-1 py-0.5 rounded transition-colors cursor-pointer"
                          data-testid={`floor-toggle-${floor.floor_id}`}
                          aria-expanded={!collapsedFloors.has(floor.floor_id)}
                        >
                          {collapsedFloors.has(floor.floor_id)
                            ? <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            : <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                          <Layers className="w-4 h-4 text-gray-400 shrink-0" />
                          <FloorNameInline floor={floor} onSaved={onFloorSaved} />
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded shrink-0" data-testid={`floor-level-${floor.floor_id}`}>
                            Niveau {floor.level}
                          </span>
                          <FloorKindInline floor={floor} onSaved={onFloorSaved} />
                          <FloorMoveButtons floor={floor} isFirst={fIdx === 0} isLast={fIdx === fArr.length - 1} onMoved={onFloorsMoved} />
                          <span className="text-xs text-gray-400 ml-auto shrink-0">
                            {(lots[floor.floor_id] || []).length} lot{(lots[floor.floor_id] || []).length > 1 ? 's' : ''}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFloor(floor);
                            setShowLotDialog(true);
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Lot
                        </Button>
                      </div>

                      {!collapsedFloors.has(floor.floor_id) && (
                        <>
                          <FloorWorkflowBar
                            floor={floor}
                            lotsInFloor={lots[floor.floor_id] || []}
                            files={files}
                            generations={generations}
                          />

                          <div className="space-y-2 ml-6">
                            {(() => {
                              const lotsArr = lots[floor.floor_id] || [];
                              const isExpanded = expandedLotsByFloor[floor.floor_id] || lotsArr.length <= LOT_PREVIEW_LIMIT;
                              const visibleLots = isExpanded ? lotsArr : lotsArr.slice(0, LOT_PREVIEW_LIMIT);
                              return (
                                <>
                                  {visibleLots.map(lot => {
                                    const currentIdx = WORKFLOW_STEPS.findIndex(s => s.key === (lot.workflow_status || 'analyse_pre_projet'));
                                    const hasRetakes = (lot.workflow_notes || []).some(n => n.type === 'retake');
                                    const genStatus = genByLot[lot.lot_id];
                                    const hasPlan = !!lot.selected_plan_file_id;
                                    const dotColor = genStatus === 'completed'
                                      ? '#22C55E'
                                      : hasPlan ? '#D9A05B' : 'rgba(255,255,255,0.18)';
                                    const dotTitle = genStatus === 'completed'
                                      ? 'Shoebox générée ✓'
                                      : hasPlan ? 'Plan retenu — prêt à générer' : 'Aucun plan retenu';
                                    return (
                                      <div
                                        key={lot.lot_id}
                                        className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                                        data-testid={`lot-card-${lot.lot_id}`}
                                      >
                                        <div
                                          role="button"
                                          tabIndex={0}
                                          onClick={() => setExpandedLotRow((cur) => (cur === lot.lot_id ? null : lot.lot_id))}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              setExpandedLotRow((cur) => (cur === lot.lot_id ? null : lot.lot_id));
                                            }
                                          }}
                                          className="w-full text-left p-2.5 flex items-center gap-2 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
                                          data-testid={`lot-row-toggle-${lot.lot_id}`}
                                          aria-expanded={expandedLotRow === lot.lot_id}
                                        >
                                          {expandedLotRow === lot.lot_id
                                            ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                            : <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                                          <span
                                            className="shrink-0 inline-flex items-center"
                                            title={dotTitle}
                                            data-testid={`lot-dot-${lot.lot_id}`}
                                          >
                                            <span
                                              className="w-2 h-2 rounded-full"
                                              style={{
                                                background: dotColor,
                                                boxShadow: genStatus === 'completed'
                                                  ? '0 0 8px rgba(34,197,94,0.7)'
                                                  : hasPlan ? '0 0 8px rgba(217,160,91,0.6)' : 'none',
                                              }}
                                            />
                                          </span>
                                          <LotThumbnail lot={lot} size={28} />
                                          <span className="text-sm font-semibold text-gray-900 shrink-0">{lot.lot_number || lot.name}</span>
                                          <span className="text-xs text-gray-500 shrink-0">{lot.typology}</span>
                                          {lot.surface > 0 && (
                                            <span className="text-xs text-gray-500 tabular-nums shrink-0">{lot.surface} m²</span>
                                          )}
                                          {lot.price > 0 && (
                                            <span className="text-xs font-medium text-gray-700 tabular-nums shrink-0" data-testid={`lot-price-${lot.lot_id}`}>
                                              {new Intl.NumberFormat('fr-FR').format(lot.price)} €
                                            </span>
                                          )}
                                          <CorrespondanceBadge
                                            correspondance={lot.correspondance}
                                            testid={`lot-correspondance-${lot.lot_id}`}
                                          />
                                          <div className="flex-1 min-w-0" />
                                          <LotStatusPill
                                            status={computeLotStatus(
                                              lot,
                                              files.filter((f) => f.lot_id === (lot.master_lot_id || lot.lot_id)),
                                              generations.filter((g) => g.lot_id === (lot.master_lot_id || lot.lot_id))
                                            )}
                                            testid={`lot-status-${lot.lot_id}`}
                                          />
                                          {hasRetakes && (
                                            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium shrink-0">Retake</span>
                                          )}
                                          <button
                                            type="button"
                                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors shrink-0"
                                            title="Éditer le lot"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedLot(lot);
                                              setLotForm({
                                                name: lot.name,
                                                lot_number: lot.lot_number,
                                                typology: lot.typology,
                                                surface: lot.surface?.toString() || '',
                                                price: lot.price?.toString() || '',
                                                style_id: lot.style_id || '',
                                                rooms_config: lot.rooms_config || [],
                                                correspondance: lot.correspondance || 'Unique',
                                              });
                                              setShowConfigDialog(true);
                                            }}
                                            data-testid={`edit-lot-${lot.lot_id}`}
                                          >
                                            <Pencil className="w-3.5 h-3.5" />
                                          </button>
                                          <button
                                            className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors shrink-0"
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              if (!window.confirm(`Supprimer le lot ${lot.lot_number || lot.name} ?`)) return;
                                              try {
                                                await axios.delete(`${API}/lots/${lot.lot_id}`, opts);
                                                setLots(prev => ({ ...prev, [floor.floor_id]: (prev[floor.floor_id] || []).filter(l => l.lot_id !== lot.lot_id) }));
                                                setAllLots(prev => prev.filter(l => l.lot_id !== lot.lot_id));
                                                toast.success('Lot supprimé');
                                              } catch { toast.error('Erreur suppression'); }
                                            }}
                                            data-testid={`delete-lot-${lot.lot_id}`}
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                        {expandedLotRow === lot.lot_id && (
                                          <div
                                            className="px-3 pb-3 pt-1 border-t bg-gray-50/40"
                                            data-testid={`lot-row-expanded-${lot.lot_id}`}
                                          >
                                            <div className="flex items-center gap-0.5 mt-2">
                                              {WORKFLOW_STEPS.map((step, i) => (
                                                <div
                                                  key={step.key}
                                                  className={`h-1.5 flex-1 rounded-full ${
                                                    i <= currentIdx ? 'bg-blue-500' : 'bg-gray-200'
                                                  } ${i === currentIdx && step.key === 'livraison_finale' ? 'bg-green-500' : ''}`}
                                                  title={step.label}
                                                />
                                              ))}
                                            </div>
                                            <p className="text-[10px] text-gray-400 mt-1">{WORKFLOW_STEPS[Math.max(0, currentIdx)]?.label}</p>
                                            <LotKpiBar
                                              lot={lot}
                                              files={files}
                                              generations={generations}
                                              onOpenFiles={(e) => {
                                                e?.stopPropagation?.();
                                                setEngTab('files');
                                              }}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                  {!isExpanded && lotsArr.length > LOT_PREVIEW_LIMIT && (
                                    <button
                                      type="button"
                                      onClick={() => setExpandedLotsByFloor((p) => ({ ...p, [floor.floor_id]: true }))}
                                      data-testid={`expand-lots-${floor.floor_id}`}
                                      className="w-full py-2 rounded-lg text-xs font-medium transition-colors"
                                      style={{
                                        background: 'rgba(217,160,91,0.10)',
                                        color: '#D9A05B',
                                        border: '1px dashed rgba(217,160,91,0.40)',
                                      }}
                                    >
                                      + Afficher les {lotsArr.length - LOT_PREVIEW_LIMIT} lots restants ({lotsArr.length} au total)
                                    </button>
                                  )}
                                  {isExpanded && lotsArr.length > LOT_PREVIEW_LIMIT && (
                                    <button
                                      type="button"
                                      onClick={() => setExpandedLotsByFloor((p) => ({ ...p, [floor.floor_id]: false }))}
                                      data-testid={`collapse-lots-${floor.floor_id}`}
                                      className="w-full py-1.5 rounded-lg text-[11px] transition-colors"
                                      style={{
                                        color: 'rgba(255,255,255,0.45)',
                                        border: '1px solid rgba(255,255,255,0.06)',
                                      }}
                                    >
                                      Réduire ({LOT_PREVIEW_LIMIT} affichés)
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </div>
                  );
                  })}

                  {(floors[building.building_id] || []).length === 0 && (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      Aucun étage. Cliquez sur "Étage" pour en ajouter.
                    </div>
                  )}
                </div>
                )}
              </div>
            ))}
          </div>
        )}
      </TabsContent>

      {/* ===== Files Tab ===== */}
      <TabsContent value="files" className="space-y-6">
        <ProjectFilesWorkspace
          projectId={projectId}
          files={files}
          setFiles={setFiles}
          buildings={buildings}
          floorsByBuilding={floors}
          allLots={allLots}
          generations={generations}
          loadingGenerations={loadingGenerations}
          onOpenStudio={() => setActiveSection('production')}
          onRefresh={async () => {
            try {
              const filesRes = await axios.get(`${API}/projects/${projectId}/files`, opts);
              setFiles(filesRes.data);
            } catch {}
            fetchGenerations();
          }}
        />
      </TabsContent>

      {/* ===== Caractéristiques techniques Tab (NEW — migré depuis Settings) ===== */}
      <TabsContent value="caracteristiques" className="space-y-5">
        <CaracteristiquesTabContent
          project={project}
          projectId={projectId}
          onProjectSaved={onProjectSaved}
        />
      </TabsContent>

      {/* ===== Catalogue Tab (NEW — migré depuis Settings) ===== */}
      <TabsContent value="catalogue" className="space-y-5">
        <CatalogueTabContent styles={styles} materials={materials} />
      </TabsContent>
    </Tabs>
  );
};

/* ============================================================ */
/* Caractéristiques tab — extracted from former ProjectSettings  */
/* ============================================================ */

export const CaracteristiquesTabContent = ({ project, projectId, onProjectSaved }) => {
  const [form, setForm] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!project) return;
    const initial = {
      project_type_code: project.project_type_code || '',
      building_count: project.building_count ?? '',
      floor_count_estimate: project.floor_count_estimate ?? '',
      is_new_construction: project.is_new_construction ?? null,
    };
    setForm(initial);
    setOriginal(initial);
  }, [project]);

  const isDirty = JSON.stringify(form) !== JSON.stringify(original);

  const setField = useCallback((k, v) => setForm((f) => ({ ...f, [k]: v })), []);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = {
        building_count: form.building_count === '' ? null : Number(form.building_count),
        floor_count_estimate: form.floor_count_estimate === '' ? null : Number(form.floor_count_estimate),
        is_new_construction: form.is_new_construction,
      };
      const r = await axios.put(`${API}/projects/${projectId}`, payload, opts);
      setOriginal(form);
      toast.success('Caractéristiques mises à jour');
      onProjectSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <>
      <HudCard variant="glass" bracket>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <span className="hud-eyebrow hud-eyebrow-gold">Caractéristiques</span>
            <p
              className="mt-1 font-semibold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}
            >
              Caractéristiques techniques du programme
            </p>
            <p className="text-[12.5px] mt-1 max-w-2xl" style={{ color: 'var(--c-text-2)' }}>
              Indications utilisées pour les estimations IA et le sizing capacity.
              Données métier — distinctes de l'identité (gérée dans Paramètres).
            </p>
          </div>
          <Boxes className="w-5 h-5 shrink-0" style={{ color: 'var(--c-gold)' }} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Labeled label="Type de projet" hint="Défini lors de la création du projet">
            <Input
              value={form.project_type_code || '—'}
              onChange={() => {}}
              disabled
              data-testid="setup-input-project-type"
              className="h-9"
            />
          </Labeled>
          <Labeled label="Nombre de bâtiments">
            <Input
              type="number"
              value={form.building_count ?? ''}
              onChange={(e) => setField('building_count', e.target.value)}
              placeholder="ex. 3"
              data-testid="setup-input-building-count"
              className="h-9"
            />
          </Labeled>
          <Labeled label="Étages (estimé)">
            <Input
              type="number"
              value={form.floor_count_estimate ?? ''}
              onChange={(e) => setField('floor_count_estimate', e.target.value)}
              placeholder="ex. 5"
              data-testid="setup-input-floor-count"
              className="h-9"
            />
          </Labeled>
        </div>

        <label className="mt-4 flex items-center gap-2.5 cursor-pointer" data-testid="setup-toggle-new-construction">
          <input
            type="checkbox"
            checked={!!form.is_new_construction}
            onChange={(e) => setField('is_new_construction', e.target.checked)}
            className="w-4 h-4 accent-[#D9A05B] cursor-pointer"
          />
          <span className="text-[13px] font-medium" style={{ color: 'var(--c-text)' }}>
            Construction neuve (programme VEFA / état futur d'achèvement)
          </span>
        </label>
      </HudCard>

      <div
        className="sticky bottom-3 z-10 rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap"
        style={{
          background: 'rgba(14,17,22,0.92)',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--c-border)',
          boxShadow: '0 12px 32px -12px rgba(0,0,0,0.6)',
        }}
        data-testid="setup-caracteristiques-save-bar"
      >
        <div className="flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--c-text-2)' }}>
          {isDirty ? (
            <>
              <AlertTriangle className="w-3.5 h-3.5" style={{ color: 'var(--c-gold)' }} />
              <span>Modifications non enregistrées</span>
            </>
          ) : (
            <>
              <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#86EFAC' }} />
              <span>À jour</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <HudButton
            variant="ghost"
            size="sm"
            onClick={() => setForm(original)}
            disabled={!isDirty || saving}
            leftIcon={<RefreshCw className="w-3.5 h-3.5" />}
            data-testid="setup-caracteristiques-reset-btn"
          >
            Annuler
          </HudButton>
          <HudButton
            variant="primary"
            size="sm"
            onClick={save}
            disabled={!isDirty || saving}
            leftIcon={saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            data-testid="setup-caracteristiques-save-btn"
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </HudButton>
        </div>
      </div>
    </>
  );
};

const Labeled = ({ label, hint, children }) => (
  <div>
    <label className="hud-eyebrow block mb-1.5">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[11px]" style={{ color: 'var(--c-text-faint)' }}>{hint}</p>}
  </div>
);

/* ============================================================ */
/* Catalogue tab — extracted from former ProjectSettings         */
/* ============================================================ */

export const CatalogueTabContent = ({ styles = [], materials = [] }) => {
  return (
    <HudCard variant="glass">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <span className="hud-eyebrow hud-eyebrow-gold">Catalogue</span>
          <p
            className="mt-1 font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}
          >
            Styles & matériaux disponibles
          </p>
          <p className="text-[12px] mt-1 max-w-2xl" style={{ color: 'var(--c-text-2)' }}>
            Bibliothèque globale réutilisée par le Studio de rendu pour
            l'attribution des matériaux/styles aux lots. La gestion est
            centralisée dans <strong>Bibliothèques → Configs prompts</strong>.
          </p>
        </div>
        <Palette className="w-5 h-5 shrink-0" style={{ color: 'var(--c-gold)' }} />
      </div>

      {styles.length === 0 && materials.length === 0 ? (
        <div
          className="rounded-lg py-10 text-center"
          style={{ background: 'var(--c-surface-2)', border: '1px dashed var(--c-border)' }}
          data-testid="setup-catalogue-empty"
        >
          <Palette className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--c-text-muted)' }} />
          <p className="text-[13px] font-medium" style={{ color: 'var(--c-text)' }}>
            Catalogue vide
          </p>
          <p className="text-[11.5px] mt-1" style={{ color: 'var(--c-text-muted)' }}>
            Ajoutez des styles et matériaux dans Bibliothèques pour les rendre disponibles ici.
          </p>
        </div>
      ) : (
        <>
          {styles.length > 0 && (
            <div className="mb-4">
              <span className="hud-eyebrow block mb-2">Styles ({styles.length})</span>
              <div className="flex flex-wrap gap-1.5" data-testid="setup-catalogue-styles">
                {styles.map((s) => (
                  <HudBadge key={s.style_id} tone={s.is_predefined === false ? 'gold' : 'neutral'}>
                    {s.name}
                  </HudBadge>
                ))}
              </div>
            </div>
          )}

          {materials.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {['sol', 'mur'].map((cat) => {
                const mats = materials.filter((m) => m.category === cat);
                if (mats.length === 0) return null;
                return (
                  <div key={cat}>
                    <span className="hud-eyebrow block mb-2">
                      {cat === 'sol' ? 'Sols' : 'Murs'} ({mats.length})
                    </span>
                    <ul className="space-y-1" data-testid={`setup-catalogue-materials-${cat}`}>
                      {mats.slice(0, 8).map((m) => (
                        <li
                          key={m.material_id}
                          className="text-[12px] flex items-center justify-between rounded px-2 py-1"
                          style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
                        >
                          <span style={{ color: 'var(--c-text)' }}>{m.name}</span>
                          <span style={{ color: 'var(--c-text-faint)' }}>{m.material_type}</span>
                        </li>
                      ))}
                      {mats.length > 8 && (
                        <li className="text-[11px] italic px-2" style={{ color: 'var(--c-text-faint)' }}>
                          + {mats.length - 8} autres…
                        </li>
                      )}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </HudCard>
  );
};

export default ProjectSetupSection;
